import { useState } from "react";
import { ChevronDown, Download, FileText } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { cn } from "~/lib/utils";
import type { EvalRun } from "~/services/evals";

/**
 * The eval harness's own HTML report, which the runner embeds whole on
 * `report.html` so it can be read without reaching into S3. It is the fullest
 * account of the run the page can offer, including per-case detail the results
 * API summarizes away.
 *
 * Rendered through `srcDoc` in a frame sandboxed with no tokens at all: the markup
 * is harness-generated, but it arrives as a stored blob and needs no trust.
 * Collapsed by default, being a denser second account of the tables above.
 */
export const RunReportCard = ({ run }: { run: EvalRun }) => {
  const [open, setOpen] = useState(false);
  const html = run.report?.html;

  if (!html) {
    return null;
  }

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <FileText className="size-4 text-muted-foreground" aria-hidden />
              Harness report
            </CardTitle>
            <p className="mt-0.5 text-sm text-muted-foreground">
              The eval harness&apos;s own rendering of this run.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadReport(run.id, html)}>
              <Download className="size-4" aria-hidden />
              Download
            </Button>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm">
                {open ? "Hide" : "Show"}
                <ChevronDown
                  className={cn("size-4 transition-transform", open && "rotate-180")}
                  aria-hidden
                />
              </Button>
            </CollapsibleTrigger>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            <iframe
              // The empty allowlist is the point: `sandbox=""` denies scripts,
              // same-origin access and form submission, so the frame can only
              // draw.
              sandbox=""
              srcDoc={html}
              title="Eval harness report"
              className="h-[540px] w-full rounded-md border border-border bg-white"
            />
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

/**
 * Saves the report as a standalone file — the one artifact shareable with someone
 * who cannot reach this admin page.
 */
function downloadReport(runId: string, html: string): void {
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `eval-run-${runId}.html`;
  link.click();
  URL.revokeObjectURL(url);
}
